import { apiRequest } from './api'

export type UserRole =
  | 'FAMILY'
  | 'PROVIDER'
  | 'AMBULANCE_PROVIDER'
  | 'USTADZ'
  | 'CEMETERY_PROVIDER'
  | 'VENDOR'
  | 'ADMIN'
  | 'FINANCE'
  | 'OPERATOR_TRAINING'
  | 'OPERATOR_OPERATION'

export interface AdminUserListItem {
  id: string
  memberId: string
  email: string
  firstName: string
  lastName: string
  phone: string | null
  role: UserRole
  isVerified: boolean
  isApproved: boolean
  isActive: boolean
  nationality: string | null
  createdAt: string
  updatedAt: string
}

export interface AdminVirtualAccount {
  number: string
  bank: string | null
  assignedAt: string
  assignedBy: { firstName: string; lastName: string } | null
}

/** One VA Balance change (informational field — not the wallet ledger). */
export interface VaBalanceLog {
  id: string
  previousBalance: number | null
  newBalance: number
  note: string | null
  createdAt: string
  changedBy: { firstName: string; lastName: string; memberId?: string } | null
}

export interface AdminUserDetail extends AdminUserListItem {
  /** True once the wallet has a balance or any PAID transaction. */
  activated?: boolean
  cardIssuedAt?: string | null
  cardIssuedBy?: string | null
  twoFactorEnabled?: boolean
  passwordResetAt?: string | null
  passportNumber: string | null
  gender: string | null
  address: string | null
  city: string | null
  state: string | null
  postalCode: string | null
  defaultCurrency: string
  profilePicture: string | null
  wallet: {
    walletNumber: string
    currency: string
    balance: string
    isFrozen: boolean
    isActive: boolean
    freezeReason: string | null
  } | null
  serviceProvider: {
    id: string
    companyName: string
    serviceType: string
    isActive: boolean
    rating: number
    reviewCount: number
  } | null
  virtualAccount: AdminVirtualAccount | null
  /** Informational VA balance (RM) — null = never set. */
  virtualAccountBalance?: number | null
  vaBalanceLogs?: VaBalanceLog[]
}

export interface UserListMeta {
  total: number
  page: number
  limit: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export interface UserListResponse {
  data: AdminUserListItem[]
  meta: UserListMeta
}

export interface UserStats {
  total: number
  active: number
  inactive: number
  pending: number
  verified: number
  byRole: { role: UserRole; count: number }[]
  signups: { today: number; last7Days: number; last30Days: number }
}

export type SignupPeriod = 'today' | '7d' | '30d'

export interface GetUsersParams {
  page?: number
  limit?: number
  search?: string
  role?: UserRole
  status?: 'active' | 'inactive' | 'pending'
  isVerified?: boolean
  signupPeriod?: SignupPeriod
}

export const usersService = {
  async getUsers(params: GetUsersParams = {}): Promise<UserListResponse> {
    const response = await apiRequest.get<UserListResponse>('/admin/users', params)
    return response.data
  },

  async getUserStats(): Promise<UserStats> {
    const response = await apiRequest.get<UserStats>('/admin/users/stats')
    return response.data
  },

  async getUserDetail(id: string): Promise<AdminUserDetail> {
    const response = await apiRequest.get<AdminUserDetail>(`/admin/users/${id}`)
    return response.data
  },

  async activateUser(id: string): Promise<void> {
    await apiRequest.patch(`/admin/users/${id}/activate`)
  },

  async deactivateUser(id: string, reason?: string): Promise<void> {
    await apiRequest.patch(`/admin/users/${id}/deactivate`, reason ? { reason } : undefined)
  },

  async setVirtualAccount(id: string, virtualAccountNumber: string, bank: string): Promise<void> {
    await apiRequest.patch(`/admin/users/${id}/virtual-account`, { virtualAccountNumber, bank })
  },

  async clearVirtualAccount(id: string): Promise<void> {
    await apiRequest.delete(`/admin/users/${id}/virtual-account`)
  },

  /** Set the informational VA balance. Same endpoint the Finance page uses. */
  async setVaBalance(id: string, balance: number, note?: string): Promise<{ unchanged: boolean }> {
    const response = await apiRequest.patch<{ unchanged: boolean }>(
      `/finance/members/${id}/va-balance`,
      { balance, note },
    )
    return response.data
  },

  /** Manual bookkeeping: record when the physical membership card was issued. The issuer is captured server-side. */
  async setCardIssue(id: string, issuedAt: string): Promise<void> {
    await apiRequest.patch(`/admin/users/${id}/card-issue`, { issuedAt })
  },

  /** Admin password reset. Clears the member's 2FA unless resetTwoFactor is false. */
  async resetPassword(
    id: string,
    newPassword: string,
    confirmPassword: string,
    resetTwoFactor: boolean,
  ): Promise<void> {
    await apiRequest.patch(`/admin/users/${id}/reset-password`, {
      newPassword,
      confirmPassword,
      resetTwoFactor,
    })
  },
}
